import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight, ArrowLeft, RefreshCw, MapPin, Home, Wallet, Heart, Calendar } from 'lucide-react';
import Seo from '@/components/Seo';

const QUESTIONS = [
  {
    id: 'goal',
    title: 'What\'s your primary goal?',
    icon: Heart,
    options: [
      { value: 'live', label: 'A home for my family', tag: 'self_use' },
      { value: 'invest', label: 'Investment for rental income', tag: 'investment' },
      { value: 'flip', label: 'Buy & sell for profit', tag: 'flipping' },
      { value: 'rent', label: 'Just looking to rent for now', tag: 'rent' },
    ],
  },
  {
    id: 'type',
    title: 'Which property type excites you?',
    icon: Home,
    options: [
      { value: 'apartment', label: 'Modern Apartment', tag: 'apartment' },
      { value: 'villa', label: 'Spacious Villa', tag: 'villa' },
      { value: 'house', label: 'Independent House', tag: 'house' },
      { value: 'plot', label: 'Land / Plot', tag: 'plot' },
      { value: 'commercial', label: 'Commercial Space', tag: 'commercial' },
    ],
  },
  {
    id: 'budget',
    title: 'What\'s your comfortable budget?',
    icon: Wallet,
    options: [
      { value: '0-50', label: 'Up to ₹50 Lakh', min: 0, max: 5000000 },
      { value: '50-100', label: '₹50 L – ₹1 Crore', min: 5000000, max: 10000000 },
      { value: '100-300', label: '₹1 Cr – ₹3 Crore', min: 10000000, max: 30000000 },
      { value: '300+', label: '₹3 Crore+', min: 30000000, max: 999999999 },
    ],
  },
  {
    id: 'city',
    title: 'Which city feels like home?',
    icon: MapPin,
    options: [
      { value: 'Mumbai', label: 'Mumbai' },
      { value: 'Bangalore', label: 'Bangalore' },
      { value: 'Delhi', label: 'Delhi NCR' },
      { value: 'Pune', label: 'Pune' },
      { value: 'Hyderabad', label: 'Hyderabad' },
      { value: 'Chennai', label: 'Chennai' },
      { value: 'any', label: 'Open to anywhere' },
    ],
  },
  {
    id: 'bhk',
    title: 'How many bedrooms do you need?',
    icon: Home,
    options: [
      { value: '1', label: '1 BHK', bedrooms: 1 },
      { value: '2', label: '2 BHK', bedrooms: 2 },
      { value: '3', label: '3 BHK', bedrooms: 3 },
      { value: '4', label: '4+ BHK', bedrooms: 4 },
      { value: 'flex', label: 'Flexible', bedrooms: null },
    ],
  },
  {
    id: 'timeline',
    title: 'When are you planning to move/invest?',
    icon: Calendar,
    options: [
      { value: 'asap', label: 'Within 1 month' },
      { value: '3m', label: '1–3 months' },
      { value: '6m', label: '3–6 months' },
      { value: 'browse', label: 'Just exploring' },
    ],
  },
];

const PropertyMatchQuizPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(-1); // -1 intro -> 0..N-1 questions -> N result
  const [answers, setAnswers] = useState({});

  const total = QUESTIONS.length;
  const progress = step >= 0 && step < total ? ((step) / total) * 100 : 0;

  const handlePick = (option) => {
    const q = QUESTIONS[step];
    setAnswers({ ...answers, [q.id]: option });
    if (step < total - 1) setStep(step + 1);
    else setStep(total); // result
  };

  const reset = () => {
    setAnswers({});
    setStep(-1);
  };

  const buildSearchUrl = () => {
    const params = new URLSearchParams();
    if (answers.goal?.tag === 'rent') params.set('listing_type', 'rent');
    else params.set('listing_type', 'buy');
    if (answers.type?.tag) params.set('property_type', answers.type.tag);
    if (answers.budget) {
      params.set('min_price', String(answers.budget.min));
      params.set('max_price', String(answers.budget.max));
    }
    if (answers.city?.value && answers.city.value !== 'any') params.set('city', answers.city.value);
    if (answers.bhk?.bedrooms) params.set('bedrooms', String(answers.bhk.bedrooms));
    return `/properties?${params.toString()}`;
  };

  // Intro screen
  if (step === -1) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="quiz-intro">
      <Seo title="Property Match Quiz" description="Take our 60-second 6-question quiz to find your perfect property in India. Personalised matches based on your goals and budget." path="/quiz" />
        <div className="container-luxury max-w-3xl">
          <div className="bg-white rounded-sm shadow-md border border-border/50 p-10 md:p-14 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-medium mb-4">
              <Sparkles className="h-3.5 w-3.5" /> 60 seconds · 6 questions
            </div>
            <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-4">
              Find Your Perfect Property
            </h1>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Answer 6 quick questions and we'll match you with properties that fit your lifestyle, budget, and goals.
            </p>
            <Button onClick={() => setStep(0)} className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-10 py-6 text-base" data-testid="start-quiz-btn">
              Start the Quiz <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Result screen
  if (step === total) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="quiz-result">
        <div className="container-luxury max-w-3xl">
          <div className="bg-white rounded-sm shadow-md border border-border/50 p-10 md:p-14">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium mb-4">
                <Sparkles className="h-3.5 w-3.5" /> Match found
              </div>
              <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-3">
                Your Bharat Estate Match
              </h1>
              <p className="text-muted-foreground">Here's what we recommend based on your answers</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <ResultRow icon={Heart} label="Goal" value={answers.goal?.label} />
              <ResultRow icon={Home} label="Property Type" value={answers.type?.label} />
              <ResultRow icon={Wallet} label="Budget" value={answers.budget?.label} />
              <ResultRow icon={MapPin} label="City" value={answers.city?.label} />
              <ResultRow icon={Home} label="Bedrooms" value={answers.bhk?.label} />
              <ResultRow icon={Calendar} label="Timeline" value={answers.timeline?.label} />
            </div>

            <div className="bg-[#0F172A] text-white rounded-sm p-6 mb-6">
              <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold mb-2">We found great matches for you</h3>
              <p className="text-white/70 text-sm mb-4">
                Browse curated {answers.type?.label?.toLowerCase() || 'properties'} in {answers.city?.value === 'any' ? 'India' : answers.city?.label} that fit your {answers.budget?.label} budget.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate(buildSearchUrl())} className="bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-bold" data-testid="see-matches-btn">
                  See Matching Properties <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button onClick={reset} variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="retake-quiz-btn">
                  <RefreshCw className="h-4 w-4 mr-2" /> Retake Quiz
                </Button>
              </div>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              Want personalised recommendations? <Link to="/dashboard" className="text-[#D4AF37] hover:underline font-medium">Sign in for AI suggestions →</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question step
  const q = QUESTIONS[step];
  const Icon = q.icon;
  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid={`quiz-step-${step}`}>
      <div className="container-luxury max-w-3xl">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>Question {step + 1} of {total}</span>
            <span>{Math.round(progress + (100 / total))}%</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#D4AF37] transition-all duration-300" style={{ width: `${progress + (100 / total)}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-sm shadow-md border border-border/50 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Icon className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <h2 className="font-['Plus_Jakarta_Sans'] text-2xl md:text-3xl font-bold text-[#0F172A]">
              {q.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {q.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handlePick(opt)}
                className="group text-left p-4 rounded-sm border-2 border-border hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all"
                data-testid={`quiz-option-${q.id}-${opt.value}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#0F172A]">{opt.label}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            ))}
          </div>

          {step > 0 && (
            <div className="mt-6 pt-6 border-t border-border/50">
              <Button onClick={() => setStep(step - 1)} variant="ghost" className="text-muted-foreground" data-testid="quiz-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ResultRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-sm">
    <Icon className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="font-semibold text-[#0F172A] truncate">{value}</p>
    </div>
  </div>
);

export default PropertyMatchQuizPage;
